// System monitoring — live operational dashboard for the reply pipeline,
// engagement sender, and creator ledger. Pulled from ass_system_health()
// every 30 seconds.

import { useSystemHealth } from '../lib/queries';

export function System() {
  const { data, isLoading, error, dataUpdatedAt } = useSystemHealth();

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section F</span>
          <span>System</span>
          <span>{dataUpdatedAt ? `last refresh ${new Date(dataUpdatedAt).toLocaleTimeString()}` : 'live'}</span>
        </div>
        <h1 className="page__title">System</h1>
        <p className="page__deck">
          Live health of the reply pipeline, engagement sender, and ledger.
          Auto-refreshes every 30 seconds.
        </p>
      </header>

      {isLoading && <p className="muted">Loading…</p>}
      {error && (
        <p className="page__deck--error">
          Health check failed: <code>{error.message}</code>
        </p>
      )}

      {data && (
        <>
          <section className="health-section">
            <h2 className="health-section__title">Reply pipeline</h2>
            <div className="health-grid">
              <Stat label="Replies received (24h)" value={data.emails_received_24h} accent={data.emails_received_24h > 0 ? 'ok' : 'idle'} />
              <Stat label="Replies received (7d)"  value={data.emails_received_7d} />
              <Stat label="Total replies"          value={data.emails_received_total} muted />
              <Stat label="Last reply at"          value={fmtDateTime(data.last_email_received_at)} small />
              <Stat label="Classifications (24h)"  value={data.classifications_24h} />
              <Stat label="Drafts pending review"  value={data.drafts_pending} accent={data.drafts_pending > 0 ? 'pending' : 'idle'} />
              <Stat label="Open escalations"       value={data.escalations_open} accent={data.escalations_open > 0 ? 'pending' : 'idle'} />
              <Stat label="Replies sent (24h)"     value={data.emails_sent_24h} />
            </div>
          </section>

          <section className="health-section">
            <h2 className="health-section__title">Stax user base — engagement universe</h2>
            <p className="health-section__deck">
              Live count of <code>auth.users</code>. Updates as users sign up or are
              removed. This is the population the engagement sender draws from —
              distinct from the creator ledger below.
            </p>
            <div className="health-grid">
              <Stat label="Total Stax users"  value={data.stax_users_total} accent="ok" />
              <Stat label="Active (30d)"      value={data.stax_users_active_30d} accent={data.stax_users_active_30d > 0 ? 'ok' : 'idle'} />
              <Stat label="Disengaged 30d+"   value={data.stax_users_disengaged_30d} accent={data.stax_users_disengaged_30d > 0 ? 'pending' : 'idle'} />
              <Stat label="Never logged in"   value={data.stax_users_never_logged_in} accent={data.stax_users_never_logged_in > 0 ? 'pending' : 'idle'} />
              <Stat label="Signups (24h)"     value={data.stax_users_signups_24h} />
              <Stat label="Signups (7d)"      value={data.stax_users_signups_7d} />
              <Stat label="Paid subscriptions" value={data.stax_paid_subscriptions} muted />
            </div>
          </section>

          <section className="health-section">
            <h2 className="health-section__title">Engagement sender</h2>
            <p className="health-section__deck">
              Outbound activity from the engagement sweep. Targets the Stax users above.
            </p>
            <div className="health-grid">
              <Stat label="Sends (24h)"      value={data.engagement_sends_24h} accent={data.engagement_sends_24h > 0 ? 'ok' : 'idle'} />
              <Stat label="Sends (7d)"       value={data.engagement_sends_7d} />
              <Stat label="Dry-run rows (24h)" value={data.engagement_dry_24h} muted />
              <Stat label="Failed (24h)"     value={data.engagement_failed_24h} accent={data.engagement_failed_24h > 0 ? 'alert' : 'idle'} />
              <Stat label="Click events (24h)" value={data.engagement_events_24h} />
              <Stat label="Last sweep at"    value={fmtDateTime(data.last_engagement_send_at)} small />
            </div>
          </section>

          <section className="health-section">
            <h2 className="health-section__title">Creator outreach ledger</h2>
            <p className="health-section__deck">
              Cold-outreach targets — finance creators on YouTube, Substack, podcasts.
              <strong> Separate</strong> from Stax users above.
            </p>
            <div className="health-grid">
              <Stat label="Total creators"   value={data.creators_total} />
              <Stat label="Replied"          value={data.creators_replied} accent="ok" />
              <Stat label="Team members"     value={data.team_members} muted />
            </div>
          </section>

          <footer className="health-foot">
            Snapshot: {fmtDateTime(data.snapshot_at)}
          </footer>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
  small,
}: {
  label: string;
  value: number | string | null;
  accent?: 'ok' | 'pending' | 'alert' | 'idle';
  muted?: boolean;
  small?: boolean;
}) {
  const v = value ?? '—';
  return (
    <div className={`health-stat ${muted ? 'health-stat--muted' : ''} ${small ? 'health-stat--small' : ''} ${accent ? 'health-stat--' + accent : ''}`}>
      <span className="health-stat__num">{typeof v === 'number' ? v.toLocaleString() : v}</span>
      <span className="health-stat__label">{label}</span>
    </div>
  );
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
